import React, { FC, useEffect, useMemo, useRef } from 'react';

import { TagCondition } from '@ariksa/inventory-core';
import { ResourceTagCondition } from '@ariksa/inventory-core/api';
import { Box, Stack, Button } from '@chakra-ui/react';
import { map, filter, isEmpty, some } from 'lodash';
import { nanoid } from 'nanoid';
import { useDispatch, useSelector } from 'react-redux';

import { Select } from 'components/DataEntry';
import { TrashIcon } from 'components/Icons';
import { useAllowListContext } from 'containers/Inventory/AllowList/context';
import { selectAllowList } from 'containers/Inventory/AllowList/selector';
import {
  deleteIconStyles,
  valueContainerStyles,
} from 'containers/PolicyHub/AlertWorkflow/styles';

import { actions } from '../../../../slice';

import { CriteriaQuantifier } from './CriteriaQuantifier';
import { RegionCriteria } from './RegionCriteria';
import { ResourceTypeCriteria } from './ResourceTypeCriteria';
import { TagKeysCriteria } from './TagKeysCriteria';
import { TagKeyValueCriteria } from './TagKeyValueCriteria';
import {
  hasConditionData,
  hasEC2ResourceCriteria,
  isCriteriaStepIncomplete,
} from './utils';

interface Props {
  setIsDisabled: (val: boolean) => void;
}

export const AllowListCriteria: FC<Props> = props => {
  const { setIsDisabled } = props;
  const {
    resetCriteria,
    resourceTypeOptions,
    tagKeyOptions,
    selectedConditions,
    setSelectedConditions,
    setHasEC2Criteria,
  } = useAllowListContext();
  const { formData, tagKeyValueCriteria, resourceTypeCriteria } = useSelector(
    selectAllowList,
  );
  const dispatch = useDispatch();

  const conditions = useMemo(
    () => [
      {
        label: 'If an entity is in region',
        value: 'region',
        component: <RegionCriteria />,
      },
      {
        label: 'If an entity has tag key',
        value: 'tag_keys_condition',
        component: <TagKeysCriteria />,
      },
      {
        label: 'If an entity has tag key and value',
        value: 'tag_key_values_condition',
        component: <TagKeyValueCriteria />,
      },
      {
        label: 'If an entity has resource type',
        value: 'resource_type_criteria',
        component: <ResourceTypeCriteria />,
      },
    ],
    [],
  );

  // Next-disabled and the EC2 flag are derived here from every row's saved
  // criteria, instead of each row overwriting one shared context flag.
  useEffect(() => {
    setIsDisabled(
      isCriteriaStepIncomplete(selectedConditions, {
        formData,
        tagKeyValueCriteria,
        resourceTypeCriteria,
      }),
    );
    setHasEC2Criteria(
      hasEC2ResourceCriteria(selectedConditions, resourceTypeCriteria),
    );
  }, [
    formData,
    selectedConditions,
    tagKeyValueCriteria,
    resourceTypeCriteria,
    setIsDisabled,
    setHasEC2Criteria,
  ]);

  // Add conditions that have saved form data without dropping conditions the
  // user picked but has not filled in yet (previously any form data change
  // rebuilt the list from form data only). The ref avoids re-running on picks.
  const selectedConditionsRef = useRef(selectedConditions);
  selectedConditionsRef.current = selectedConditions;
  useEffect(() => {
    const current = selectedConditionsRef.current;
    const options = filter(
      conditions,
      o =>
        some(current, { value: o.value }) ||
        hasConditionData(formData, o.value),
    );
    if (options.length !== current.length) setSelectedConditions(options);
  }, [conditions, formData, setSelectedConditions]);

  return (
    <Stack>
      <CriteriaQuantifier />

      <Stack>
        <Box mb={2}>
          <Select
            options={conditions}
            value={selectedConditions}
            isMulti
            showTotalSelected={selectedConditions.length > 0}
            placeholder="Choose conditions..."
            onChange={selected => {
              setSelectedConditions(selected);
              // Seed resource type and tag key + value rows independently, so
              // picking both at once seeds both (was an else-if).
              if (
                some(selected, { value: 'resource_type_criteria' }) &&
                isEmpty(formData?.criteria?.resource_type_criteria)
              )
                dispatch(
                  actions.updateResourceTypeCriteria({
                    id: nanoid(),
                    value: {
                      resource_type: resourceTypeOptions[0]?.value,
                      key_condition: ResourceTagCondition.HasResourceType,
                    },
                  }),
                );
              if (
                some(selected, { value: 'tag_key_values_condition' }) &&
                isEmpty(formData?.criteria?.tag_key_values_condition)
              )
                dispatch(
                  actions.updateTagKeyValuesCriteria({
                    id: nanoid(),
                    value: {
                      key: tagKeyOptions[0]?.value,
                      key_condition: TagCondition.Tagged,
                      value: [],
                      value_condition: TagCondition.Tagged,
                    },
                  }),
                );
            }}
          />
        </Box>
        {map(selectedConditions, each => (
          <Stack
            {...valueContainerStyles}
            key={each?.value + '-environment-condition'}
          >
            <Box w="full">{each?.component}</Box>
            <Button
              {...deleteIconStyles}
              onClick={() => {
                setSelectedConditions(
                  filter(selectedConditions, o => o.value !== each.value),
                );
                resetCriteria(each.value);
              }}
            >
              <TrashIcon />
            </Button>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};
